import React, { useState } from 'react';
import { find, isNil, prop, propEq, propOr } from 'ramda';
import { Icon, InfoTooltip } from '@fasstech/middle-design-system';
import classNames from 'classnames';
import { mapIndexed } from '@@serverHelpers/index.mjs';
import { getGarantyIcon } from '../helpers/getGarantyIcon.jsx';

const Module = ({
  moduleCode,
  displayedPricings,
  selectedPricingCodes,
  findModuleLabel,
  findModuleGuarantees
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const label = findModuleLabel(moduleCode);
  const guarantees = findModuleGuarantees(moduleCode);

  return (
    <div
      className={
        'grid border rounded-xl shadow-lg [&:first-of-type]:mt-0 w-full'
      }
      style={{
        gridTemplateColumns: `300px repeat(${displayedPricings.length}, minmax(150px, 1fr))`
      }}
    >
      {/* HEADER ROW */}
      <div
        className={classNames(
          'col-span-full flex h-14 items-center justify-between',
          {
            'border-b': isOpen
          }
        )}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className={'px-4 py-3 font-bold'}>
          <div className="flex gap-4 items-center">
            <Icon name={getGarantyIcon(moduleCode)} />
            {label}
          </div>
        </div>
        <div className={'px-4 py-3'}>
          <Icon
            name="down_arrow"
            size={20}
            wrapperClassName={classNames(
              'transition-transform cursor-pointer ',
              {
                'rotate-180 -translate-x-1/4 -translate-y-1/4': isOpen
              }
            )}
            /*TODO use theme*/
            fillClassName="#202326"
          />
        </div>
      </div>

      {/* TABLE ROWS */}
      {isOpen &&
        mapIndexed(({ code, isTitle }, index) => {
          const isLast = index === guarantees.length - 1;
          let note;
          for (const pricing of displayedPricings) {
            const module = find(
              (module) => module.moduleCode === moduleCode,
              propOr([], 'modules', pricing)
            );
            if (!module) {
              continue;
            }
            const guarantee = find(
              (guarantee) => guarantee.guaranteeCode === code,
              propOr([], 'guarantees', module)
            );
            if (!guarantee) {
              continue;
            }
            if (guarantee.guaranteeNote) {
              note = guarantee.guaranteeNote;
              break;
            }
          }
          return (
            // {/* Guarantee label column */}
            <div className={'contents'}>
              <div
                className={classNames(
                  'sticky left-0 z-10 p-2 min-h-20 ml-2 flex items-center',
                  {
                    'border-b': !isLast,
                    'font-bold': isTitle
                  }
                )}
              >
                {code}
                {note && (
                  <InfoTooltip
                    className="ml-2 !inline [&>div]:!inline [&_svg]:!inline"
                    text={note}
                  />
                )}
              </div>

              {mapIndexed(({ modules, pricingLabel }) => {
                const module = find(propEq('moduleCode', moduleCode))(modules);
                const guarantee = find(propEq('guaranteeCode', code))(
                  propOr([], 'guarantees', module)
                );

                const isSelected = !isNil(
                  find((val) => val === pricingLabel)(selectedPricingCodes)
                );

                const isTitle = prop('isTitle', guarantee);
                return (
                  <div
                    key={pricingLabel}
                    className={classNames(
                      'flex items-center justify-center text-center border-r border-l',
                      isSelected
                        ? 'bg-primary-300 border-white min-h-full m-y-4'
                        : 'border-gray-50 bg-white rounded'
                    )}
                  >
                    {!isTitle && (
                      <span
                        className={classNames(
                          'flex justify-center items-center w-7/8 px-2 rounded min-h-12 font-bold bg-[#EBF4F4]',
                          {
                            'text-[#4ED64B]!': guarantee?.ccnStatus === 'ok',
                            'text-red-600': guarantee?.ccnStatus === 'ko',
                            'text-black': guarantee?.ccnStatus === 'equal'
                          }
                        )}
                      >
                        {guarantee?.value || '-'}
                        {guarantee?.valueNote && (
                          <InfoTooltip
                            text={guarantee.valueNote}
                            className={classNames('absolute')}
                          />
                        )}
                      </span>
                    )}
                  </div>
                );
              })(displayedPricings)}
            </div>
          );
        })(guarantees)}
    </div>
  );
};

export default Module;
